// Angular
import { computed, effect, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

// RxJS
import { fromEventPattern } from 'rxjs';

// UI Components
import { ThemeMode } from '@sms-ir/ui-components';


@Injectable()
export class ThemeService {
  readonly mode = signal<ThemeMode>('system');

  private readonly query = window.matchMedia('(prefers-color-scheme: dark)');

  private readonly systemDark = toSignal(
    fromEventPattern<boolean>((handler) => {
      const listener = (e: MediaQueryListEvent) => handler(e.matches);
      this.query.addEventListener('change', listener);
      return () => this.query.removeEventListener('change', listener);
    }),
    {initialValue: this.query.matches}
  );

  readonly effectiveTheme = computed(() => {
    const userChoice = this.mode();
    if (userChoice === 'system') {
      return this.systemDark() ? 'dark' : 'light';
    }
    return userChoice;
  });

  constructor() {
    effect(() => {
      const theme = this.effectiveTheme();
      this.applyThemeToDocument(theme);
    });
  }

  private applyThemeToDocument(theme: Omit<ThemeMode, "system">) {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme as string);
  }

  setTheme(newMode: ThemeMode) {
    this.mode.set(newMode);
  }
}
